import { useState, useCallback, useEffect, useRef } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKAlert from "components/MKAlert";
import MKInput from "components/MKInput";
import SEO from "components/SEO";

// SEO structured data
import { buildBreadcrumbSchema } from "config/structuredData";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Booking sections
import AvailabilityCalendar from "pages/Booking/sections/AvailabilityCalendar";
import PricingSummary from "pages/Booking/sections/PricingSummary";
import GuestInfoForm from "pages/Booking/sections/GuestInfoForm";
import HouseRules from "pages/Booking/sections/HouseRules";
import BookingTerms from "pages/Booking/sections/BookingTerms";
import Amenities from "pages/Home/sections/Amenities";

// Data & API
import pricingDataDefaults, { calculateTotal } from "pages/Booking/sections/data/pricingData";
import propertyData from "pages/Home/sections/data/propertyData";
import { submitBookingRequest, fetchPricing } from "api";
import { isPromoStay } from "config/promo";
import { trackMetaEvent } from "utils/metaEvents";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Google Ads "Booking Request" conversion — fires when a booking request is
// submitted (Google Ads > Goals > Conversions).
const GOOGLE_ADS_BOOKING_CONVERSION = "AW-18373399093/2W-0CI_L1dwcELWkj7lE";

function Booking() {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(pricingDataDefaults);
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    countryCode: "+1",
    phone: "",
    guests: "",
    children: "0",
    specialRequests: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [guestsTouched, setGuestsTouched] = useState(false);
  const initiateCheckoutFired = useRef(false);

  // Fetch pricing config from API (falls back to pricingData.js defaults)
  useEffect(() => {
    fetchPricing()
      .then((data) => setPricingConfig({ ...pricingDataDefaults, ...data }))
      .catch(() => {});
  }, []);

  // Fire Meta InitiateCheckout once the guest reaches a priced selection.
  useEffect(() => {
    if (pricing && !initiateCheckoutFired.current) {
      initiateCheckoutFired.current = true;
      trackMetaEvent("InitiateCheckout");
    }
  }, [pricing]);

  // Recalculate pricing whenever dates or guest count change
  const recalcPricing = useCallback(
    (start, end, guests) => {
      if (start && end) {
        const oneDay = 24 * 60 * 60 * 1000;
        const nights = Math.round((end - start) / oneDay);
        const minNights = pricingConfig.minimumNights || 5;
        const maxNights = pricingConfig.maximumNights || 21;

        if (nights < minNights) {
          setDateError(`Minimum stay is ${minNights} nights. Please select a longer stay.`);
          setPricing(null);
        } else if (nights > maxNights) {
          setDateError(`Maximum stay is ${maxNights} nights. Please select a shorter stay.`);
          setPricing(null);
        } else {
          setDateError(null);
          setPricing(calculateTotal(start, end, pricingConfig, guests));
        }
      } else {
        setPricing(null);
        setDateError(null);
      }
    },
    [pricingConfig]
  );

  const handleDatesSelected = useCallback(
    (start, end) => {
      setCheckIn(start);
      setCheckOut(end);
      const guests = guestInfo.guests;
      if (guests && parseInt(guests, 10) > 0) {
        recalcPricing(start, end, guests);
      } else {
        setPricing(null);
      }
    },
    [recalcPricing, guestInfo.guests]
  );

  // Update group size and recalculate pricing if dates are already selected
  const handleGroupSizeChange = useCallback(
    (field, value) => {
      setGuestInfo((prev) => {
        const updated = { ...prev, [field]: value };
        if (field === "guests") {
          recalcPricing(checkIn, checkOut, updated.guests);
        }
        return updated;
      });
    },
    [recalcPricing, checkIn, checkOut]
  );

  const handleGuestInfoChange = useCallback((newGuestInfo) => {
    setGuestInfo(newGuestInfo);
  }, []);

  const depositPct = Math.round((pricingConfig.minimumDepositPercent || 0.5) * 100);
  const balancePct = 100 - depositPct;
  const maxGuests = pricingConfig.maxGuests || 16;
  const hasGroupSize = guestInfo.guests && parseInt(guestInfo.guests, 10) > 0;

  const isFormValid =
    pricing &&
    guestInfo.name.trim() &&
    guestInfo.email.trim() &&
    guestInfo.phone.trim() &&
    guestInfo.guests &&
    termsAccepted;

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { bookingId } = await submitBookingRequest({
        checkIn: checkIn.toISOString().split("T")[0],
        checkOut: checkOut.toISOString().split("T")[0],
        guestInfo,
        pricing,
      });
      setConfirmDialog(true);
      trackMetaEvent("CompleteRegistration", {
        email: guestInfo.email,
        phone: `${guestInfo.countryCode || ""}${guestInfo.phone || ""}`,
        name: guestInfo.name,
      });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: GOOGLE_ADS_BOOKING_CONVERSION,
          value: 1.0,
          currency: "USD",
          transaction_id: bookingId || "",
        });
      }
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Book Your Stay"
        description="Check availability and book Stalley Beach House directly — save 15–20% vs Airbnb with no booking fees. Reserve your beachfront Costa Rica villa with a 50% deposit."
        path="/booking"
        imageAlt="Book Stalley Beach House, a beachfront villa in Costa Rica"
        schema={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Book Now", path: "/booking" },
        ])}
      />
      <DefaultNavbar routes={routes} sticky />
      <MKBox
        minHeight="25vh"
        width="100%"
        variant="gradient"
        bgColor="info"
        sx={{
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <MKTypography variant="h2" component="h1" color="white" textAlign="center">
            Book Your Stay
          </MKTypography>
          <MKTypography variant="body1" color="white" textAlign="center" opacity={0.8} mt={1}>
            {propertyData.name} &middot; {propertyData.location.city}, {propertyData.location.state}
          </MKTypography>
          <MKTypography variant="body2" color="white" textAlign="center" opacity={0.6} mt={0.5}>
            Book direct and save 15-20% vs Airbnb
          </MKTypography>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 3,
          mx: { xs: 2, lg: 3 },
          mt: -6,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Step 1: Group size */}
        <MKBox component="section" py={4}>
          <Container maxWidth="sm">
            <MKTypography variant="h5" mb={1}>
              <Icon sx={{ mr: 1, verticalAlign: "middle" }}>group</Icon>
              Group Size
            </MKTypography>
            <MKTypography
              variant="body2"
              color="dark"
              fontWeight="bold"
              mb={3}
              sx={{ fontStyle: "italic" }}
            >
              *Enter your group size to see pricing.*
            </MKTypography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MKBox
                  sx={
                    !guestsTouched && !guestInfo.guests
                      ? {
                          "@keyframes glowPulse": {
                            "0%, 100%": { boxShadow: "0 0 6px 2px rgba(26,115,232,0.3)" },
                            "50%": { boxShadow: "0 0 14px 4px rgba(26,115,232,0.5)" },
                          },
                          animation: "glowPulse 2s ease-in-out infinite",
                          borderRadius: "8px",
                        }
                      : {}
                  }
                >
                  <MKInput
                    type="number"
                    label={`Total Guests (max ${maxGuests})`}
                    fullWidth
                    value={guestInfo.guests}
                    onChange={(e) => handleGroupSizeChange("guests", e.target.value)}
                    onFocus={() => setGuestsTouched(true)}
                    inputProps={{ min: 1, max: maxGuests }}
                    required
                  />
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Step 2: Select dates (always visible, prices shown when group size set) */}
        <AvailabilityCalendar
          onDatesSelected={handleDatesSelected}
          pricingConfig={pricingConfig}
          numGuests={hasGroupSize ? guestInfo.guests : 0}
          hidePrices={!hasGroupSize}
        />

        {/* Prompt to enter group size if dates selected without it */}
        {checkIn && checkOut && !hasGroupSize && !dateError && (
          <MKBox py={2}>
            <Container maxWidth="sm">
              <MKAlert color="info">
                <Icon sx={{ mr: 1 }}>group</Icon>
                Please enter the number of guests to see pricing.
              </MKAlert>
            </Container>
          </MKBox>
        )}

        {/* Seasonal free-van offer: shown when the selected check-in qualifies */}
        {checkIn && checkOut && isPromoStay(checkIn) && (
          <MKBox py={1}>
            <Container maxWidth="sm">
              <MKBox
                display="flex"
                alignItems="flex-start"
                gap={1.5}
                p={2.5}
                borderRadius="lg"
                sx={({ palette: { success }, functions: { rgba } }) => ({
                  backgroundColor: rgba(success.main, 0.1),
                  border: "1px solid",
                  borderColor: rgba(success.main, 0.3),
                })}
              >
                <Icon sx={{ color: "success.main", fontSize: "1.6rem !important", mt: 0.2 }}>
                  directions_car
                </Icon>
                <MKBox>
                  <MKTypography variant="body2" fontWeight="bold" color="dark">
                    Your dates qualify &mdash; the brand-new 2027 Staria van is included free!
                  </MKTypography>
                  <MKTypography variant="caption" color="text" display="block" mt={0.5}>
                    Van rental only. Two-way airport transfers and the Toyota RAV4 are separate paid
                    add-ons.
                  </MKTypography>
                </MKBox>
              </MKBox>
            </Container>
          </MKBox>
        )}

        {/* Date range warning */}
        {dateError && (
          <MKBox py={2}>
            <Container maxWidth="sm">
              <MKAlert color="warning">
                <Icon sx={{ mr: 1 }}>info</Icon>
                {dateError}
              </MKAlert>
            </Container>
          </MKBox>
        )}

        {/* Step 3: Price breakdown */}
        {pricing && (
          <>
            <Divider sx={{ my: 2 }} />
            <PricingSummary pricing={pricing} />
          </>
        )}

        {/* Step 3: Guest info (without group size fields) */}
        {pricing && (
          <>
            <Divider sx={{ my: 2 }} />
            <GuestInfoForm guestInfo={guestInfo} onChange={handleGuestInfoChange} />
          </>
        )}

        {/* House rules & terms */}
        {pricing && (
          <>
            <Divider sx={{ my: 2 }} />
            <HouseRules pricingConfig={pricingConfig} />
            <BookingTerms
              accepted={termsAccepted}
              onAcceptChange={setTermsAccepted}
              pricingConfig={pricingConfig}
            />
          </>
        )}

        {/* Submit */}
        {pricing && (
          <MKBox py={0}>
            <Container maxWidth="sm">
              {submitError && (
                <MKAlert color="error" dismissible sx={{ mb: 2 }}>
                  {submitError}
                </MKAlert>
              )}
              <MKButton
                variant="gradient"
                color="info"
                size="large"
                fullWidth
                disabled={!isFormValid || submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Submitting..." : "Request to Book"}
              </MKButton>
              <MKTypography
                variant="caption"
                color="dark"
                display="block"
                textAlign="center"
                mt={1}
              >
                You won&apos;t be charged yet. We&apos;ll review your request and send a payment
                link.
              </MKTypography>
            </Container>
          </MKBox>
        )}

        {/* Amenities */}
        <Divider sx={{ my: 3 }} />
        <Amenities />

        {/* General Policy */}
        <Divider sx={{ my: 3 }} />
        <MKBox mx="auto" maxWidth="1200px">
          <MKTypography variant="h5" mb={2} textAlign="center">
            <Icon sx={{ mr: 1, verticalAlign: "middle" }}>policy</Icon>
            General Policy
          </MKTypography>
          <MKTypography variant="body2" color="dark" mb={1}>
            We want you to have a great time, and will work hard to see that happens, but for
            security purposes we do not allow bachelor parties, weddings, or outside guests on to
            the property, that you may meet, or know in Costa Rica, that are not a part of your
            arriving group. My wife, our son, and myself live on the property in another smaller
            house.
          </MKTypography>
          <MKTypography variant="body2" color="dark" mb={1}>
            Guests agrees to show passport ID upon arrival at the property, and sign vacation rental
            agreement for agreed upon dates.
          </MKTypography>
          <MKTypography variant="body2" color="dark" mb={1}>
            The maid and kayaks are only available for a stay of 5 or more days. The cook is usually
            available for an extra cost.
          </MKTypography>
          <MKTypography variant="body2" color="dark">
            User/Guest shall comply with all statutes, ordinances and requirements of all municipal,
            national authorities now in force, or which may hereafter be in force, pertaining to the
            use of the premises.
          </MKTypography>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>

      {/* Booking Confirmation Dialog */}
      <Dialog open={confirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <MKBox display="flex" alignItems="center" gap={1}>
            <Icon color="success">check_circle</Icon>
            Request Submitted!
          </MKBox>
        </DialogTitle>
        <DialogContent>
          <MKTypography variant="body2" mb={2}>
            Your booking request has been submitted. Here&apos;s what happens next:
          </MKTypography>
          <MKBox display="flex" gap={1} mb={1.5}>
            <Icon sx={{ color: "info.main", mt: 0.3, flexShrink: 0 }}>looks_one</Icon>
            <MKTypography variant="body2" color="dark">
              <strong>We review your request</strong> — you&apos;ll hear back within 24 hours.
            </MKTypography>
          </MKBox>
          <MKBox display="flex" gap={1} mb={1.5}>
            <Icon sx={{ color: "info.main", mt: 0.3, flexShrink: 0 }}>looks_two</Icon>
            <MKTypography variant="body2" color="dark">
              <strong>Once approved</strong>, we&apos;ll email you a secure payment link.
            </MKTypography>
          </MKBox>
          <MKBox display="flex" gap={1}>
            <Icon sx={{ color: "info.main", mt: 0.3, flexShrink: 0 }}>looks_3</Icon>
            <MKTypography variant="body2" color="dark">
              <strong>Pay a {depositPct}% deposit</strong> to confirm. The remaining {balancePct}%
              is due on arrival.
            </MKTypography>
          </MKBox>
        </DialogContent>
        <DialogActions>
          <MKButton
            variant="gradient"
            color="info"
            onClick={() => {
              setConfirmDialog(false);
              window.location.href = "/";
            }}
          >
            Back to Home
          </MKButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Booking;
